import {
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  TextInputSelectionChangeEventData,
  TextStyle,
} from 'react-native';
import { MentionsInputFieldMobile } from './mentionFieldMobile';
import { MentionsInputFieldWeb } from './mentionFieldWeb';

export type MentionInputFieldProps = {
  inputFieldText: string;
  setInputFieldText: (text: string) => void;
  setIsMultiline: (value: boolean) => void;
  inputHeight: number;
  setInputHeight: (value: number) => void;
  send: () => void;
  suggestionsPosition?: 'top' | 'bottom'; // Position suggestions above or below input
  handleEnter?: boolean;
  placeholder?: string;
  /** Mobile only: focus the input (and open the keyboard) on mount. */
  autoFocus?: boolean;
  /** Mobile only: notified when the input loses focus. */
  onBlur?: () => void;
  /** Mobile only: style overrides applied to the visible input AND the hidden
   *  height-measurement mirrors, so line wrapping stays consistent. */
  inputTextStyle?: StyleProp<TextStyle>;
  /** Mobile only: how far (px) the suggestion panel extends past the input's
   *  right edge. Defaults to the legacy pill layout's 84 (covers the
   *  send-button column); pass 0 when the input spans the full row. */
  suggestionsRightOverflow?: number;
  /** Mobile only: reports caret / selection moves, in PLAIN-text offsets. */
  onSelectionChange?: (
    event: NativeSyntheticEvent<TextInputSelectionChangeEventData>
  ) => void;
  /** Mobile only: pins the caret; leave undefined during normal typing. */
  selection?: { start: number; end: number };
  /**
   * The activity being composed in, if any. Scopes the mentionable list to
   * include that activity's live grant holders (e.g. a guest) in addition to
   * ordinary workspace members. Omit entirely for a not-yet-created activity
   * (the new-activity composer) — it has no grant holders to add.
   */
  issueId?: string;
};

export const MentionInput = (props: MentionInputFieldProps) => {
  const {
    inputFieldText,
    setInputFieldText,
    setIsMultiline,
    inputHeight,
    setInputHeight,
    send,
    suggestionsPosition,
    handleEnter,
    placeholder,
    autoFocus,
    onBlur,
    inputTextStyle,
    suggestionsRightOverflow,
    onSelectionChange,
    selection,
    issueId,
  } = props;

  const isWeb = Platform.OS === 'web';

  const renderMobileMentionInput = () => (
    <MentionsInputFieldMobile
      inputFieldText={inputFieldText}
      setInputFieldText={setInputFieldText}
      setIsMultiline={setIsMultiline}
      inputHeight={inputHeight}
      setInputHeight={setInputHeight}
      suggestionsPosition={suggestionsPosition}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onBlur={onBlur}
      inputTextStyle={inputTextStyle}
      suggestionsRightOverflow={suggestionsRightOverflow}
      onSelectionChange={onSelectionChange}
      selection={selection}
      issueId={issueId}
    />
  );

  const renderWebMentionInput = () => (
    <MentionsInputFieldWeb
      inputFieldText={inputFieldText}
      setInputFieldText={setInputFieldText}
      send={send}
      suggestionsPosition={suggestionsPosition}
      handleEnter={handleEnter}
      placeholder={placeholder}
      setInputHeight={setInputHeight}
      setIsMultiline={setIsMultiline}
      issueId={issueId}
    />
  );

  return isWeb ? renderWebMentionInput() : renderMobileMentionInput();
};
